import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { DEFAULT_STEPS } from './constants';
import {
  ProjectDetails,
  Step,
  StepsData,
  StepsId,
  StrategyOptions,
  TeamType,
} from './types';
import { getNextIndex } from './utils';

export enum APIStatus {
  Idle = 'idle',
  Loading = 'loading',
  Success = 'success',
  Error = 'error',
}

const INITIAL_DATA: TeamPlannerState = {
  selectedStep: DEFAULT_STEPS[0],
  previousStep: DEFAULT_STEPS[0],
  steps: DEFAULT_STEPS,
  stepsData: null,
  isPlaceholderModalShown: false,
  selectedProject: null,
  selectedStrategy: StrategyOptions.LoadBalanced,
  isStrategySelectionStepShown: false,
  goToNextStepStatus: APIStatus.Idle,
  findTeamMembersStatus: APIStatus.Idle,
  assignAndAllocateStatus: APIStatus.Idle,
  rowsWithUsersRemovedManually: [],
};

export interface TeamPlannerState {
  stepsData: StepsData | null;
  selectedStep: Step;
  previousStep: Step | null;
  steps: Step[];
  isPlaceholderModalShown: boolean;
  selectedProject: ProjectDetails | null;
  selectedStrategy: StrategyOptions;
  isStrategySelectionStepShown: boolean;
  goToNextStepStatus: APIStatus;
  findTeamMembersStatus: APIStatus;
  assignAndAllocateStatus: APIStatus;
  rowsWithUsersRemovedManually: number[];
}

type NewPlaceholder = {
  teams: TeamType[];
};

export const teamPlannerSlice = createSlice({
  initialState: INITIAL_DATA,
  name: 'teamPlanner',
  reducers: {
    setStepsData: (
      state: TeamPlannerState,
      { payload }: PayloadAction<StepsData>,
    ) => {
      return { ...state, stepsData: { ...state.stepsData, ...payload } };
    },
    updateField: (state: TeamPlannerState, { payload }) => {
      return {
        ...state,
        stepsData: {
          ...state.stepsData,
          rows:
            state.stepsData?.rows?.map((row) =>
              (row.team?.teamId ?? row.user?.userId) === payload.id
                ? {
                    ...row,
                    [payload.field]: payload.newData,
                  }
                : row,
            ) ?? [],
        },
      };
    },
    updateAllUnresolvedFields: (state: TeamPlannerState, { payload }) => {
      return {
        ...state,
        stepsData: {
          ...state.stepsData,
          rows:
            state.stepsData?.rows?.map((row) =>
              !row.resolved
                ? {
                    ...row,
                    [payload.field]: payload.newData,
                  }
                : row,
            ) ?? [],
        },
      };
    },
    addNewPlaceholder: (
      state: TeamPlannerState,
      { payload }: PayloadAction<NewPlaceholder>,
    ) => {
      const newRows = payload.teams?.map((team) => ({
        team: {
          teamId: team.teamId,
          teamName: team.teamName,
          roleId: team.roleId,
        },
        dateEditable: true,
        allocatedMinutes: 0,
        resolved: false,
        isNoMatchFoundShown: false,
      }));

      return {
        ...state,
        stepsData: {
          ...state.stepsData,
          rows: state.stepsData?.rows?.concat(newRows) ?? [],
        },
      };
    },
    togglePlaceholderModal: (state: TeamPlannerState) => {
      return {
        ...state,
        isPlaceholderModalShown: !state.isPlaceholderModalShown,
      };
    },
    setSelectedProject: (state: TeamPlannerState, { payload }) => {
      return {
        ...state,
        selectedProject: payload,
      };
    },
    setSelectedStrategy: (
      state: TeamPlannerState,
      { payload }: PayloadAction<StrategyOptions>,
    ) => {
      return {
        ...state,
        selectedStrategy: payload,
      };
    },
    setSelectedStep: (
      state: TeamPlannerState,
      { payload }: PayloadAction<Step>,
    ) => {
      return {
        ...state,
        selectedStep: payload,
      };
    },
    setPreviousStep: (
      state: TeamPlannerState,
      { payload }: PayloadAction<Step>,
    ) => {
      return {
        ...state,
        previousStep: payload,
      };
    },
    addSteps: (state: TeamPlannerState, { payload }: PayloadAction<Step[]>) => {
      return {
        ...state,
        steps: payload,
      };
    },
    removeStep: (
      state: TeamPlannerState,
      { payload }: PayloadAction<{ id: StepsId }>,
    ) => {
      return {
        ...state,
        steps: state.steps.filter((step) => step.id !== payload.id),
      };
    },
    resetAllData: (state: TeamPlannerState) => {
      return {
        ...state,
        selectedStep: DEFAULT_STEPS[0],
        selectedStrategy: StrategyOptions.LoadBalanced,
        isStrategySelectionStepShown: true,
        selectedProject: null,
        stepsData: null,
        rowsWithUsersRemovedManually: [],
      };
    },
    resetSteps: (state: TeamPlannerState) => {
      return {
        ...state,
        steps: DEFAULT_STEPS,
      };
    },

    goToNextStepIdle: (state: TeamPlannerState) => {
      return {
        ...state,
        goToNextStepStatus: APIStatus.Idle,
      };
    },
    goToNextStepLoading: (state: TeamPlannerState) => {
      return {
        ...state,
        goToNextStepStatus: APIStatus.Loading,
      };
    },
    goToNextStepFailure: (state: TeamPlannerState) => {
      return {
        ...state,
        goToNextStepStatus: APIStatus.Error,
      };
    },
    goToNextStepSuccess: (
      state: TeamPlannerState,
      { payload }: PayloadAction<StepsData>,
    ) => {
      return {
        ...state,
        goToNextStepStatus: APIStatus.Success,
        stepsData: { ...state.stepsData, ...payload },
      };
    },
    goToNextStep: (state: TeamPlannerState) => {
      const nextIndex = getNextIndex(state.steps, state.selectedStep);
      const nextStep = state.steps[nextIndex];

      return {
        ...state,
        goToNextStepStatus: APIStatus.Success,
        selectedStep: nextStep,
      };
    },
    setFindTeamMemberStatus: (
      state: TeamPlannerState,
      { payload }: PayloadAction<{ status: APIStatus }>,
    ) => {
      return {
        ...state,
        findTeamMembersStatus: payload.status,
      };
    },
    setAssignAndAllocateStatus: (
      state: TeamPlannerState,
      { payload }: PayloadAction<{ status: APIStatus }>,
    ) => {
      return {
        ...state,
        assignAndAllocateStatus: payload.status,
      };
    },
    addRowsWithUsersRemovedManually: (
      state: TeamPlannerState,
      { payload }: PayloadAction<{ id: number }>,
    ) => {
      return {
        ...state,
        rowsWithUsersRemovedManually: state.rowsWithUsersRemovedManually.concat(
          payload.id,
        ),
      };
    },
    removeRowsWithUsersRemovedManually: (
      state: TeamPlannerState,
      { payload }: PayloadAction<{ id: number }>,
    ) => {
      return {
        ...state,
        rowsWithUsersRemovedManually: state.rowsWithUsersRemovedManually.filter(
          (id) => id !== payload.id,
        ),
      };
    },
    resetRowsWithUsersRemovedManually: (state: TeamPlannerState) => {
      return {
        ...state,
        rowsWithUsersRemovedManually: [],
      };
    },
  },
});
